/**
 * Required-slot autofill on the CART (HITL-1551).
 *
 * WHY ON THE CART. An escalated car stands the AI down, so its own pre-submit
 * autofill never runs. An unfilled REQUIRED mod group fails the whole item tree
 * at the Menu Module, and on HITL's side `getValidCartItems` (cart.ts:778) drops
 * the item before it even leaves the client — so nothing reaches the POS and
 * staff have nothing to correct.
 *
 * Filling the cart rather than the outgoing order payload is what lets the whole
 * feature reuse `sendOrder` unchanged: once the slots are filled the items are
 * valid, `getValidCartItems` keeps them, and every downstream behaviour —
 * order-metadata assembly, coupon/loyalty side effects, request tracking,
 * menu-module error UI — comes along for free. It also means the agent's cart
 * and the `agent_cart` event show what was filled instead of it being invisible.
 *
 * WHAT THIS IS NOT. The first port of this ticket rebuilt the AI's five-step
 * `_send_order_impl` chain over `IBrandMenuOrderItem`. Four of those steps turned
 * out to be inert on a HITL cart — HITL never produces a '?', a REPLACE_ME, an
 * `is_coupon` constraint, an info-Q sentinel, or a coupon ITEM (coupons live in
 * `cart.coupons` / `cart.loyaltyCouponItem` and travel as order metadata) — and
 * the fifth, min-quantity, is a single `minimumSelections` read here. What is
 * left is the fill ladder itself, which is what this module is.
 *
 * INVARIANTS (the ticket's acceptance criteria — do not lose them):
 *   1. Never replace a value already on the cart. Only groups HITL itself calls
 *      invalid are touched, and only to make up their shortfall.
 *   2. Never invent a slot. Every group filled here came off the item's own
 *      resolved `modifierGroups`, which `buildFullMenuUnifiedItem` already
 *      narrowed to this store, this meal period, and what is not 86'd. That
 *      narrowing is per ITEM, though: an option the brand menu marks
 *      unavailable only for one choice (banana blizzard as a Blizzard's
 *      flavour) still reads `available`, so the ladder also asks the same
 *      per-choice question the agent's picker asks (HITL-1697).
 *   3. Required only (`minimumSelections > 0`). Optional groups are left alone on
 *      purpose: they do not block the send, and their first option is often a
 *      paid add-on.
 *   4. When every option is unavailable, fill the first one anyway. An unfilled
 *      required slot means nothing reaches the POS at all; a possibly-86'd option
 *      still puts the order on staff's screen (MOT-17018).
 *   5. No options at all: give up, do not invent a value.
 */

import { IBatchedModifierSelection } from '../reducers/cartSlice';
import { UPSELL, UPSELL_PREFIX } from '../constants';
import { IConstraint } from '../types/brandMenu';
import { ParsedMenuItem, ParsedModifierGroup } from '../types/menu';
import { UnavailableItemsByName } from '../redux/features/menuUnification/menuUnification.props';
import { CartItem, CartModifierGroup, isInvalidModGroup } from './cart';
import { ModSymbolCodeNameMappingType } from './mappings';
import { CONSTRAINT_DEFAULT_FALLBACK_ITEM } from './cartAutofill.constants';
import logger from './logger';
import { isBMItemConstraintNotAvailable } from './menuUnification.utils';
import { GenericMap } from './types';

export interface ICartAutofillDeps {
  /** Brand-menu constraints, keyed by name. Read for two things the parsed
   * group does not carry: `constraint_default_fallback_item`, which
   * `parseModifierGroup` does not lift out of `conditions`, and the sheet's
   * `child_items` order, which it replaces with alphabetical order for display.
   * Everything else the ladder needs is already on the group. */
  brandMenuConstraints?: GenericMap<IConstraint>;
  modSymbolMapping: ModSymbolCodeNameMappingType;
  posSettings: GenericMap<any>;
  suppressUpsellEnabled?: boolean;
  /** Brand-menu / restaurant unavailability, keyed by the entry it applies to
   * (HITL-1697). Its per-choice entries (`constraints: { blizzard_flavor:
   * ['banana blizzard'] }` under `blizzard`) are invisible to `available`,
   * which is per item — a fill that ignores them is rejected by the Menu Module
   * as `unavailable_brand_items` and takes the whole final with it. Omitted
   * (the non-unified menu) means no per-choice check, as before. */
  unavailableItemsByName?: UnavailableItemsByName;
  /** Suppress the per-fill [AUTOFILL] logs. The trigger-time gate
   * (`selectNeedsAutofill`, HITL-1562) runs this same ladder only to ask
   * "would filling change anything?" — logging that dry run would double every
   * fill line the thunk's real run then logs. */
  quiet?: boolean;
}

export interface ICartAutofillResult {
  /** Ready to hand to `cartActions.selectModifiers`. Empty when nothing needed
   * filling, which is the common case. */
  selections: IBatchedModifierSelection[];
  /** One `modGroup=item` entry per fill — the "fills are logged" criterion. */
  fills: string[];
  /** Required groups the ladder could not fill. These still block the send, and
   * naming them is the difference between a diagnosable failure and a silent one. */
  unfillable: string[];
  /** Unanswered `/upsell` carrier items to DELETE from the cart instead of
   * filling (HITL-1652). A carrier is a question, not order content: the ladder
   * would resolve its group to the decline option, and the order build promotes
   * a carrier's selection to a top-level item — so the fill shipped a literal
   * top-level `decline`, which the menu module 400s. Removal is what the manual
   * decline click already does (CartItemDetail.onMenuItemModifierSelect). */
  carrierRemovals: { cartItemId: number; name: string }[];
}

/** An `/upsell` carrier item — the slash-command entry point an upsell is
 * offered through, matched exactly the way the manual decline guard matches it
 * (CartItemDetail.tsx). Its selection gets PROMOTED to a top-level order item
 * by `buildUnifiedMenuModuleOrder`, which is why it must never be autofilled. */
const isUpsellCarrier = (name: string | undefined): boolean =>
  !!name && (name.startsWith(UPSELL) || name.startsWith(UPSELL_PREFIX));

/** A node that carries modifier groups: a cart item, or a menu option that has
 * not been selected yet. Only the fields the ladder reads. */
type GroupBearingItem = Pick<ParsedMenuItem, 'name' | 'itemId'> & {
  modifierGroups?: GenericMap<ParsedModifierGroup>;
  /** What is already selected under it — present on a cart item or a chosen
   * option, absent on an option being added. Read by the per-choice
   * unavailability check for multi-constraint entries. */
  childModifierGroups?: GenericMap<CartModifierGroup>;
  pathToItem?: string;
};

/**
 * Is `option` unavailable as `owner`'s choice for `modGroup` (HITL-1697)?
 *
 * The item builder's own check: `isBMModifierAnUnavailableConstraint`, which
 * crosses the option out, delegates to exactly this call. That wrapper is not
 * used here because it finds the owner by walking the cart by `pathToItem`,
 * and an option the fill is ADDING is not in the cart yet — it would silently
 * fall back to the top-level item and look up the wrong entry (the meal deal's
 * instead of `explicit blizzard choice`'s). So the owner is passed in directly.
 */
const isUnavailableForChoice = (
  owner: GroupBearingItem | undefined,
  modGroup: ParsedModifierGroup,
  option: ParsedMenuItem,
  deps: ICartAutofillDeps
): boolean =>
  !!owner &&
  !!deps.unavailableItemsByName &&
  isBMItemConstraintNotAvailable({
    unavailableItemsByName: deps.unavailableItemsByName,
    itemName: option.name,
    cartItem: owner as unknown as CartItem,
    constraintName: modGroup.name,
  });

/**
 * `constraint_default_fallback_item:<value>` off the constraint's hand-authored
 * `conditions`, validated against the group's own options so a stale menu value
 * cannot be injected. Same flattening idiom as `parseModifierGroup`
 * (menuUnification.utils.ts:531): one condition string can carry several
 * newline-separated tags.
 */
export const parseConstraintDefaultFallbackItem = (
  constraintDef?: IConstraint
): string | undefined => {
  if (!constraintDef) return undefined;

  const prefix = `${CONSTRAINT_DEFAULT_FALLBACK_ITEM}:`;
  const tag = (constraintDef.conditions ?? [])
    .flatMap((condition) => condition.split('\n'))
    .map((condition) => condition.trim())
    .find((condition) => condition.toLowerCase().startsWith(prefix));
  if (!tag) return undefined;

  const fallback = tag.slice(prefix.length).trim();
  return fallback || undefined;
};

/** The options a group offers, in display (alphabetical) order. */
const optionsOf = (modGroup: ParsedModifierGroup): ParsedMenuItem[] =>
  Object.values(modGroup.menuItems ?? {});

/** The options in the sheet's `child_items` order — the last-resort pick. Sheet
 * authors tend to list the standard option first, which makes it a better
 * arbitrary fill than the alphabet (HITL-1682). An option the constraint does
 * not list keeps its display position after the listed ones. */
const optionsInSheetOrder = (
  modGroup: ParsedModifierGroup,
  constraintDef?: IConstraint
): ParsedMenuItem[] => {
  const options = optionsOf(modGroup);
  const childItems = constraintDef?.child_items;
  if (!childItems?.length) return options;

  const rank = new Map(
    childItems.map((name, index) => [name.toLowerCase(), index])
  );
  const rankOf = (option: ParsedMenuItem) =>
    rank.get(String(option.name ?? '').toLowerCase()) ?? childItems.length;
  return [...options].sort((a, b) => rankOf(a) - rankOf(b));
};

/** Resolves a name from the menu (a default id, a fallback tag, a declined item)
 * to one of the group's actual options. Case-insensitive on both `itemId` and
 * `name`, and returning undefined for a name the group does not offer is what
 * validates a stale menu value out. */
const resolveOption = (
  modGroup: ParsedModifierGroup,
  name?: string | number | null
): ParsedMenuItem | undefined => {
  if (name === undefined || name === null) return undefined;
  const wanted = String(name).trim().toLowerCase();
  if (!wanted) return undefined;
  return optionsOf(modGroup).find(
    (option) =>
      String(option.itemId ?? '').toLowerCase() === wanted ||
      String(option.name ?? '').toLowerCase() === wanted
  );
};

/**
 * The precedence ladder: the configured default, then the
 * `constraint_default_fallback_item` condition, then `upsell_declined_item`,
 * then the sheet's `child_items` order.
 *
 * Rungs 2 and 3 are also what resolves an UNANSWERED UPSELL to its decline
 * rather than to a paid child item — the AI runs that as a separate step ahead
 * of the fill, but on a ladder that tries the declined item before the option
 * list it falls out for free.
 *
 * Rung 4 really does hand out an arbitrary pick: 27 of DQ's 164 required
 * constraints carry no default of any kind, mostly flavour choices. That is the
 * intended trade — an arbitrary flavour on the POS beats an order staff never
 * see — and it is why every fill is logged.
 */
export const pickFillOption = (
  modGroup: ParsedModifierGroup,
  deps: ICartAutofillDeps,
  context: string,
  /** Options already on the group, or already picked for it in this pass. A
   * `min > 1` group needs that many DISTINCT options — `selectedItems` is keyed
   * by `itemId`, so handing back the same one twice leaves the group short. */
  exclude: Set<string> = new Set(),
  /** The item or option that owns `modGroup`, for the per-choice
   * unavailability check (HITL-1697). */
  owner?: GroupBearingItem
): ParsedMenuItem | undefined => {
  const constraintDef = deps.brandMenuConstraints?.[modGroup.name];

  const ordered: Array<ParsedMenuItem | undefined> = [
    ...(modGroup.defaultSelectedItemIds ?? []).map((id) =>
      resolveOption(modGroup, id)
    ),
    resolveOption(modGroup, parseConstraintDefaultFallbackItem(constraintDef)),
    resolveOption(modGroup, modGroup.upsell_declined_item),
    ...optionsInSheetOrder(modGroup, constraintDef),
  ];

  const seen = new Set<string>();
  const candidates: ParsedMenuItem[] = [];
  ordered.forEach((option) => {
    if (!option) return;
    const key = String(option.itemId ?? option.name ?? '').toLowerCase();
    if (!key || seen.has(key) || exclude.has(key)) return;
    seen.add(key);
    candidates.push(option);
  });

  if (!candidates.length) {
    if (!deps.quiet)
      logger.warn(
        `[AUTOFILL] Required group '${modGroup.name}' has no options (${context}); cannot fill`
      );
    return undefined;
  }

  // `available` is computed per option when the tree is built (parseROPMenuItem,
  // menuUnification.utils.ts:275) and already covers BOTH item-level signals —
  // out-of-stock/not-sold and an inactive item meal period (HITL-1290). It
  // cannot see an option that is unavailable only as THIS choice, so that is
  // asked separately (HITL-1697): 36 finals in a week were rejected because
  // the fill picked banana blizzard for a flavour the agent had just been
  // refused.
  const available = candidates.find(
    (option) =>
      option.available !== false &&
      !isUnavailableForChoice(owner, modGroup, option, deps)
  );
  if (available) {
    if (available !== candidates[0] && !deps.quiet) {
      logger.info(
        `[AUTOFILL] Preferred option '${candidates[0].name}' for required group '${modGroup.name}' is unavailable; using '${available.name}' instead (${context})`
      );
    }
    return available;
  }

  // Invariant 4.
  if (!deps.quiet)
    logger.warn(
      `[AUTOFILL] Every option for required group '${modGroup.name}' is unavailable (${context}); filling '${candidates[0].name}' anyway -- the availability feed is unreliable here, so this may still land, but expect a Menu Module availability rejection if the feed was right`
    );
  return candidates[0];
};

/**
 * How many selections a required group is short, or 0 when it needs nothing.
 *
 * Gated on HITL's own `isInvalidModGroup` (cart.ts:140) — the same predicate
 * `getValidCartItems` and the Finish Order button use — so this can never
 * disagree with HITL about which groups are a problem. The shortfall is then
 * computed separately because `isInvalidModGroup` is also true for an
 * OVER-selected group, which is not ours to "fix".
 */
export const requiredShortfall = (
  modGroup: ParsedModifierGroup,
  cartModGroup?: CartModifierGroup
): number => {
  if (!isInvalidModGroup(modGroup, cartModGroup)) return 0;

  const minimum = modGroup.minimumSelections ?? 0;
  if (minimum <= 0) return 0; // Invariant 3.

  const selected = Object.keys(cartModGroup?.selectedItems ?? {}).length;
  return selected < minimum ? minimum - selected : 0;
};

/**
 * Walks a cart's items and returns the selections that would complete every
 * unfilled required slot. Pure — it applies nothing.
 */
export const buildCartAutofillSelections = (
  cartItems: GenericMap<CartItem> | undefined,
  deps: ICartAutofillDeps
): ICartAutofillResult => {
  const fills: string[] = [];
  const unfillable: string[] = [];

  /** Fills every required group of an option we are ADDING. Nothing is selected
   * under it yet, so each required group is short its full minimum. */
  const fillNewOption = (
    cartItemId: number,
    option: GroupBearingItem,
    depth: number
  ): IBatchedModifierSelection[] => {
    const children: IBatchedModifierSelection[] = [];
    // A menu tree can loop; nothing legitimate nests this deep.
    if (depth > 12) {
      // Report it. Returning silently left a required slot neither filled nor
      // flagged, so the outcome read `filled_and_submitted` while the item was
      // dropped at submit with nothing to diagnose it by.
      if (!deps.quiet)
        logger.warn(
          `[AUTOFILL] Stopped descending at '${option.name}' (depth ${depth})`
        );
      unfillable.push(`${option.name} (nesting depth ${depth})`);
      return children;
    }

    Object.values(option.modifierGroups ?? {}).forEach((modGroup) => {
      const minimum = modGroup.minimumSelections ?? 0;
      if (minimum <= 0) return; // Invariant 3.
      children.push(
        ...buildSelections(
          cartItemId,
          modGroup,
          minimum,
          depth,
          new Set(),
          option
        )
      );
    });
    return children;
  };

  /**
   * One selection per missing slot, each a DIFFERENT option.
   *
   * `isInvalidModGroup` counts `Object.keys(selectedItems)`, and that map is
   * keyed by `itemId` — so a "choose 2" group is satisfied by two distinct
   * options, never by one option at quantity 2. Emitting a single selection with
   * `quantity: shortfall` left the group short, `getValidCartItems` dropped the
   * whole item, and `fills` still reported success.
   */
  const buildSelections = (
    cartItemId: number,
    modGroup: ParsedModifierGroup,
    shortfall: number,
    depth: number,
    alreadyOnGroup: Set<string>,
    owner: GroupBearingItem
  ): IBatchedModifierSelection[] => {
    const context = `group '${modGroup.name}'`;
    const nodes: IBatchedModifierSelection[] = [];
    const taken = new Set(alreadyOnGroup);

    for (let i = 0; i < shortfall; i += 1) {
      const option = pickFillOption(modGroup, deps, context, taken, owner);
      if (!option) {
        // Invariant 5. Also the honest answer when a `min > 1` group simply does
        // not offer enough distinct options to satisfy itself.
        unfillable.push(modGroup.name);
        break;
      }
      taken.add(String(option.itemId ?? option.name ?? '').toLowerCase());

      fills.push(`${modGroup.name}=${option.name}`);
      if (!deps.quiet)
        logger.info(
          `[AUTOFILL] Filling required group '${modGroup.name}' with '${
            option.name
          }' (${i + 1} of ${shortfall})`
        );

      nodes.push({
        selection: {
          cartItemId,
          menuItemId: option.itemId,
          modGroupId: modGroup.id,
          selected: true,
          // No mod code: the fill states a value, it does not modify one.
          modCode: '',
          modSymbolMapping: deps.modSymbolMapping,
          posSettings: deps.posSettings,
          quantity: 1,
          pathToItem: option.pathToItem,
          suppressUpsellEnabled: deps.suppressUpsellEnabled,
        },
        // Deliberately NO `unavailableConstraintCheck`. The hypothesis path
        // passes one so the AI cannot propose an 86'd modifier; here it would
        // silently drop the very fill invariant 4 exists to force through,
        // turning a rescued order back into no order at all. Avoiding an
        // unavailable choice is the ladder's job, at pick time (HITL-1697).
        children: fillNewOption(cartItemId, option, depth + 1),
      });
    }
    return nodes;
  };

  /** Walks a node already ON the cart: fill what is short, descend into what is
   * already chosen. */
  const walkExisting = (
    cartItemId: number,
    item: GroupBearingItem,
    childModifierGroups: GenericMap<CartModifierGroup> | undefined,
    out: IBatchedModifierSelection[],
    depth: number
  ): void => {
    if (depth > 12) {
      if (!deps.quiet)
        logger.warn(
          `[AUTOFILL] Stopped descending at '${item.name}' (depth ${depth})`
        );
      unfillable.push(`${item.name} (nesting depth ${depth})`);
      return;
    }

    Object.values(item.modifierGroups ?? {}).forEach((modGroup) => {
      const cartModGroup = childModifierGroups?.[modGroup.id];
      const shortfall = requiredShortfall(modGroup, cartModGroup);

      if (shortfall > 0) {
        const alreadyOnGroup = new Set(
          Object.values(cartModGroup?.selectedItems ?? {}).map((chosen: any) =>
            String(chosen?.itemId ?? chosen?.name ?? '').toLowerCase()
          )
        );
        out.push(
          ...buildSelections(
            cartItemId,
            modGroup,
            shortfall,
            depth,
            alreadyOnGroup,
            item
          )
        );
      }

      // Descend into what the agent/AI already chose, whether or not this group
      // itself needed topping up — a chosen option can own an unfilled slot.
      Object.values(cartModGroup?.selectedItems ?? {}).forEach((selected) =>
        walkExisting(
          cartItemId,
          selected as GroupBearingItem,
          (selected as CartItem).childModifierGroups,
          out,
          depth + 1
        )
      );
    });
  };

  const selections: IBatchedModifierSelection[] = [];
  const carrierRemovals: { cartItemId: number; name: string }[] = [];
  Object.values(cartItems ?? {}).forEach((cartItem) => {
    if (!cartItem?.cartItemId) return;

    // HITL-1652: an upsell carrier whose question is still unanswered is a
    // decline, and a declined carrier is REMOVED, never filled — filling its
    // group lands on the decline option, which the order build then promotes to
    // a top-level `decline` item the menu module rejects with 400
    // invalid_items. This mirrors the manual decline click, which deletes the
    // carrier instead of selecting the option.
    //
    // "Unanswered" means NO selection anywhere on the carrier — not "some
    // required group is short". The order build promotes the FIRST constraint's
    // selection (menuUnification.ts), so a carrier holding any selection was
    // answered; removing it would delete something the customer said yes to if
    // a carrier ever carried a second, unrelated required group. An answered
    // carrier is left to the ordinary walk, so any remaining short group on it
    // is filled like any other item's.
    if (isUpsellCarrier(cartItem.name)) {
      const unanswered = !Object.values(
        cartItem.childModifierGroups ?? {}
      ).some(
        (cartModGroup) =>
          Object.keys(cartModGroup?.selectedItems ?? {}).length > 0
      );
      if (unanswered) {
        carrierRemovals.push({
          cartItemId: cartItem.cartItemId,
          name: cartItem.name,
        });
        if (!deps.quiet)
          logger.info(
            `[AUTOFILL] Removing unanswered upsell carrier '${cartItem.name}' instead of filling it — an unanswered upsell is a decline, and a declined carrier never reaches the order (HITL-1652)`
          );
        return;
      }
    }

    walkExisting(
      cartItem.cartItemId,
      cartItem as GroupBearingItem,
      cartItem.childModifierGroups,
      selections,
      0
    );
  });

  return { selections, fills, unfillable, carrierRemovals };
};
